/**
 * NOC-PROD-UI-1 — Painel de Produção (read-only).
 *
 * Visão operacional de produção/release: firmware vs release-alvo, proteção OTA
 * (denylist), HMAC/secure_channel, auth_secret, SF1 (mac_anchor/clone), watchdog,
 * scheduler, config_revision, comandos pendentes e rollouts não-terminais.
 *
 * 100% somente-leitura: NENHUM botão dispara OTA/comando/rollback/config. Os
 * únicos controles são "Atualizar" (refetch) e leitura. Fonte: 1 endpoint
 * agregado GET /api/admin/fleet/production-status.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import StatCard from "../components/StatCard.jsx";
import { getFleetProduction } from "../lib/api.js";
import { formatDate, formatRelative } from "../lib/format.js";
import { getProtectionState } from "../lib/protection.js";

export default function ProductionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const res = await getFleetProduction({ signal });
      setData(res);
      setLastRefresh(Date.now());
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Erro ao carregar painel de produção");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    load(ctl.signal);
    return () => ctl.abort();
  }, [load, refreshKey]);

  const devices = useMemo(() => data?.devices || [], [data]);
  const target = data?.target_release_version || null;
  const protectedIds = useMemo(() => data?.protected_device_ids || [], [data]);

  const isOnTarget = useCallback(
    (d) => !!target && !!d.fw_version && d.fw_version === target,
    [target],
  );

  const summary = useMemo(() => {
    const s = {
      total: devices.length,
      online: 0,
      offline: 0,
      protected: protectedIds.length,
      divergent: 0,
      hmacBroken: 0,
      clones: 0,
      preSf1: 0,
      pending: 0,
    };
    for (const d of devices) {
      if (d.online) s.online += 1;
      else s.offline += 1;
      if (!isOnTarget(d)) s.divergent += 1;
      if (!d.secure_channel) s.hmacBroken += 1;
      if (d.clone_suspected) s.clones += 1;
      if (d.mac_anchor_status !== "ok") s.preSf1 += 1;
      s.pending += d.pending_commands_count || 0;
    }
    return s;
  }, [devices, protectedIds, isOnTarget]);

  const denylistEmpty = protectedIds.length === 0;

  return (
    <div className="space-y-4">
      {/* HEADER */}
      <section className="card-pad space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-mono text-sm font-semibold uppercase tracking-[0.18em] text-noc-text">
            Painel de Produção
          </h1>
          <span className="badge-info">somente leitura</span>
          <div className="ml-auto flex items-center gap-3 text-xs text-noc-muted">
            {lastRefresh && (
              <span>
                atualizado: <span className="font-mono text-noc-text">{formatDate(lastRefresh)}</span>
              </span>
            )}
            <button
              type="button"
              className="px-3 py-1 rounded border border-noc-border text-noc-text hover:bg-noc-bg/50 disabled:opacity-50"
              disabled={loading}
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              {loading ? "Atualizando…" : "Atualizar"}
            </button>
          </div>
        </div>
        <div className="text-xs text-noc-muted">
          Release-alvo:{" "}
          <span className="font-mono text-noc-text">{target || "—"}</span>
          {data?.active_rollouts > 0 && (
            <span className="ml-3 badge-warn">{data.active_rollouts} rollout(s) ativo(s)</span>
          )}
        </div>
        {denylistEmpty && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
            ⚠ Nenhum device protegido contra OTA (denylist vazia em runtime) — produção EXPOSTA.
            Setar <span className="font-mono">OTA_PROTECTED_DEVICE_IDS</span> no ambiente.
          </div>
        )}
        {error && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 break-words">
            {error}
          </div>
        )}
      </section>

      {/* RESUMO — StatCards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Frota ativa" value={summary.total} />
        <StatCard label="Online" value={summary.online} tone="ok" />
        <StatCard label="Offline" value={summary.offline} tone={summary.offline ? "warn" : "default"} />
        <StatCard label="Protegidos OTA" value={summary.protected} tone="info" />
        <StatCard label="Fora do alvo" value={summary.divergent} tone={summary.divergent ? "warn" : "ok"} />
        <StatCard label="HMAC quebrado" value={summary.hmacBroken} tone={summary.hmacBroken ? "err" : "ok"} />
        <StatCard label="Clone suspeito" value={summary.clones} tone={summary.clones ? "err" : "ok"} />
        <StatCard label="Pré-SF1" value={summary.preSf1} tone={summary.preSf1 ? "warn" : "ok"} />
        <StatCard label="Cmds pendentes" value={summary.pending} tone={summary.pending ? "warn" : "ok"} />
        <StatCard label="Rollouts ativos" value={data?.active_rollouts ?? 0} tone={data?.active_rollouts ? "warn" : "ok"} />
      </div>

      {/* DENYLIST */}
      <section className="card-pad space-y-2">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          Devices protegidos contra OTA (denylist)
        </h2>
        {denylistEmpty ? (
          <div className="text-sm text-noc-err">Nenhum — produção exposta.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {protectedIds.map((id) => {
              const dev = devices.find((d) => d.device_id === id);
              return (
                <span key={id} className="badge-ok font-mono" title={dev ? `fw ${dev.fw_version}` : "não visto em heartbeat"}>
                  🔒 {id}
                  {dev ? (dev.online ? " ●" : " ○") : " ?"}
                </span>
              );
            })}
          </div>
        )}
      </section>

      {/* TABELA PRINCIPAL */}
      <section className="card-pad space-y-3">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          Frota — estado por device
        </h2>
        {loading && devices.length === 0 ? (
          <div className="text-sm text-noc-muted">Carregando…</div>
        ) : devices.length === 0 ? (
          <div className="text-sm text-noc-muted">Nenhum device ativo.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint border-b border-noc-border">
                  <th className="py-2 pr-3">device_id</th>
                  <th className="py-2 pr-3">firmware</th>
                  <th className="py-2 pr-3 text-center">online</th>
                  <th className="py-2 pr-3 text-center">OTA</th>
                  <th className="py-2 pr-3 text-center">HMAC</th>
                  <th className="py-2 pr-3 text-center">secret</th>
                  <th className="py-2 pr-3 text-center">anchor</th>
                  <th className="py-2 pr-3">watchdog</th>
                  <th className="py-2 pr-3 text-center">sched</th>
                  <th className="py-2 pr-3 text-right">cfg</th>
                  <th className="py-2 pr-3 text-center">pend</th>
                  <th className="py-2 pr-3">last_seen</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => {
                  const onTarget = isOnTarget(d);
                  const preSf1 = d.mac_anchor_status !== "ok";
                  return (
                    <tr
                      key={d.device_id}
                      className="border-b border-noc-border/30 hover:bg-noc-bg/40"
                    >
                      <td className="py-2 pr-3 font-mono">{d.device_id}</td>
                      <td className="py-2 pr-3 font-mono">
                        <span className={onTarget ? "text-noc-ok" : "text-noc-warn"}>
                          {d.fw_version || "unknown"}
                        </span>
                        {!onTarget && <span className="ml-1 text-[10px] text-noc-warn">div</span>}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <span className={d.online ? "text-noc-ok" : "text-noc-faint"}>
                          {d.online ? "●" : "○"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {getProtectionState(d).protected ? (
                          <span className="badge-ok">🔒</span>
                        ) : (
                          <span className="text-noc-faint">livre</span>
                        )}
                        {d.has_pending_ota && <span className="ml-1 text-noc-warn" title="OTA pendente">●</span>}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <span className={d.secure_channel ? "text-noc-ok" : "text-noc-err"}>
                          {d.secure_channel ? "✓" : "✗"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <span className={d.auth_secret_set ? "text-noc-ok" : "text-noc-err"}>
                          {d.auth_secret_set ? "✓" : "✗"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-center font-mono text-xs">
                        {d.clone_suspected ? (
                          <span className="badge-err">CLONE?</span>
                        ) : (
                          <span className={preSf1 ? "text-noc-warn" : "text-noc-ok"}>
                            {d.mac_anchor_status || "pré-SF1"}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {d.watchdog_enabled === false ? (
                          <span className="badge-err">OFF</span>
                        ) : d.watchdog_max_fails != null ? (
                          <span className="text-noc-muted">
                            mf={d.watchdog_max_fails}/{d.watchdog_timeout_window_s ?? "?"}s
                          </span>
                        ) : (
                          <span className="text-noc-faint">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center font-mono">
                        <span className={d.scheduler_version >= 2 ? "text-noc-ok" : "text-noc-warn"}>
                          {d.scheduler_version ?? "—"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-noc-muted">
                        {d.config_revision ?? "—"}
                      </td>
                      <td className="py-2 pr-3 text-center font-mono">
                        <span className={d.pending_commands_count ? "text-noc-warn" : "text-noc-faint"}>
                          {d.pending_commands_count}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-noc-muted whitespace-nowrap">
                        {d.last_seen ? formatRelative(d.last_seen) : "nunca"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-xs text-noc-muted">
          Online: last_seen &lt; {data?.online_window_s ?? "?"}s. Read-only — nenhuma ação dispara OTA/comando.
        </div>
      </section>
    </div>
  );
}
